package structs.accelbyte.promo

import kotlinx.serialization.Serializable
import structs.accelbyte.common.AccelByteJson
import structs.accelbyte.common.AccelByteSerializable
import structs.account.SubscriptionAddOn

/**
 * One granted-item row as AGS returned it on a `RedeemCode` call.
 *
 * `extraSubscriptionDays` is non-null only for items that AGS treats as
 * subscription extensions; for non-subscription grants it is null. The
 * `quantity` field reflects `RedeemableItem.quantity` from the underlying
 * campaign definition.
 */
@Serializable
data class PromoCodeRedeemedItem(
    val itemId: String,
    val itemName: String,
    val quantity: Int,
    val extraSubscriptionDays: Int? = null,
    val outcomeType: PromoOutcomeType = if (extraSubscriptionDays != null)
        PromoOutcomeType.SUBSCRIPTION_MONTHS else PromoOutcomeType.CREDITS_GRANT,
    val rewardAmount: Int = quantity,
)

/**
 * Response of `server.extend.redeemPromoCode`.
 *
 * On success, `availability = AVAILABLE` and `grantedItems` carries every
 * item AGS handed to the user (typically one row per `RedeemableItem` on the
 * underlying campaign).
 *
 * On a single-use redemption that has already been consumed, `availability
 * = REDEEMED` and `grantedItems` is empty -- AGS does not re-grant on a second
 * `RedeemCode` call, and the proxy mirrors that contract.
 *
 * On AGS error the response surfaces `availability = UNKNOWN` and an
 * `errorMessage`; the caller may treat that as a generic "code not usable"
 * without distinguishing server-side errors from client typos.
 */
@Serializable
data class RedeemPromoCodeResponse(
    val availability: PromoCodeAvailability,
    val grantedItems: List<PromoCodeRedeemedItem> = emptyList(),
    /**
     * After Phase-2 (2026-08-20) the redeem path also persists an
     * additive subscription add-on row to the player's
     * [structs.account.AccountSettings]. This list carries the
     * player's complete `addOns` array *after* the redemption --
     * useful for the operator dashboard to render the resulting
     * effective subscription surface without a second cloud-save
     * read.
     */
    val persistedAddOns: List<SubscriptionAddOn> = emptyList(),
    val persistedCredits: Double? = null,
    val operationId: String? = null,
    val errorMessage: String? = null
) : AccelByteSerializable {
    override fun toJsonString(): String = AccelByteJson.encodeToString(serializer(), this)
}
